/**
 * The price. One constant, with its currency and its tax treatment attached.
 *
 * > ⚠️ **IT WAS 3.14 ﷼ UNTIL 2026-09-02, AND THE PUN IS THE ONLY THING LOST.** π priced a product
 * > nobody had bought yet; 5.00 ﷼ is a round number in the currency the customer thinks in, which is
 * > worth more at a checkout than a joke only we were in on. Recorded rather than deleted because
 * > the figure appears in prose all over this repository and the next person to find a stale 3.14
 * > should know it is stale rather than wonder which one is right.
 *
 * ⚠️ A NUMBER WITH A CURRENCY, NOT A NUMBER. "5" on its own gets rendered next to a `$` by the first
 * person who builds a pricing card in a hurry, and a Saudi customer shown a dollar price is a Saudi
 * customer who does not buy.
 *
 * ⚠️ **`inclusive: true` IS A LEGAL FACT, NOT A CONFIGURATION.** Saudi VAT is 15% and consumer prices
 * must be displayed **inclusive** of it — ZATCA enforces this and the fines are per-instance. So
 * 5.00 ﷼ is the gross figure a customer pays; the net is 4.35 ﷼ and the tax is 0.65 ﷼. Any invoice
 * this product ever generates must show that split, which is why the split is computed here rather
 * than left for whoever writes the invoice template.
 *
 * ⚠️ MINOR UNITS (halalas), NOT FLOATS. A price that divides cleanly today does not stay that way —
 * the VAT split of 500 is 435 and 65, and `4.35 * 12` is `52.199999999999996` in IEEE 754. A
 * subscription total a hundredth of a halala off is a support ticket nobody can reproduce.
 */

export type Price = {
  amountMinor: number
  currency: 'SAR'
  per: 'site-month'
  vatBasisPoints: number
  inclusive: boolean
  split(): { net: number; vat: number; gross: number }
  format(lang: 'en' | 'ar', minor?: number): string
  amountOnly(lang: 'en' | 'ar', minor?: number): string
}

export const PRICE: Price = {
  /** 5.00 ﷼ = 500 halalas. */
  amountMinor: 500,
  currency: 'SAR' as const,
  /** The unit the amount buys. */
  per: 'site-month' as const,
  /** Saudi VAT, in basis points. 15% = 1500. */
  vatBasisPoints: 1500,
  /** Whether `amountMinor` already contains the VAT. In Saudi Arabia, for a consumer price, it must. */
  inclusive: true,

  /** The net and tax components of an inclusive price, in minor units. Rounded half-up on the tax so
   *  net + tax === gross exactly, which is the property an invoice needs and naive rounding breaks. */
  split(): { net: number; vat: number; gross: number } {
    const gross = PRICE.amountMinor
    const net = Math.round((gross * 10000) / (10000 + PRICE.vatBasisPoints))
    return { net, vat: gross - net, gross }
  },

  /** For display. `Intl` is present in every runtime this package targets. */
  format(lang: 'en' | 'ar', minor = PRICE.amountMinor): string {
    return new Intl.NumberFormat(lang === 'ar' ? 'ar-SA' : 'en-SA', {
      style: 'currency',
      currency: PRICE.currency,
      // ⚠️ `latn` FOR ARABIC TOO, and that is a considered choice rather than an oversight. Saudi
      // interfaces overwhelmingly show Western digits; Eastern Arabic numerals on a price is the
      // detail that makes a site read as translated rather than as Saudi.
      numberingSystem: 'latn',
    }).format(minor / 100)
  },

  /**
   * Just the digits, for a UI that sizes the number and its currency differently.
   *
   * ⚠️ `formatToParts`, NOT A REGEX OVER `format()` — and this replaces exactly such a regex, which
   * shipped a visible bug. Stripping "everything that is not a digit or a dot" is correct for
   * `SAR 3.14` and wrong for the Arabic form `٣٫١٤ ر.س.`, whose currency abbreviation contains two
   * full stops: the price rendered as `3.14..` on every Arabic screen. `Intl` already knows which
   * characters are the number; asking it is both shorter and right in locales nobody here has
   * thought about.
   */
  amountOnly(lang: 'en' | 'ar', minor = PRICE.amountMinor): string {
    const parts = new Intl.NumberFormat(lang === 'ar' ? 'ar-SA' : 'en-SA', {
      style: 'currency',
      currency: PRICE.currency,
      numberingSystem: 'latn',
    }).formatToParts(minor / 100)

    return parts
      .filter((part) => ['integer', 'group', 'decimal', 'fraction', 'minusSign'].includes(part.type))
      .map((part) => part.value)
      .join('')
  },
}

/**
 * The free week.
 *
 * ⚠️ PUBLISHING IS FREE AND STAYING PUBLISHED IS NOT, WHICH IS A DIFFERENT FUNNEL FROM THE ONE THIS
 * PRODUCT STARTED WITH. It used to be *build free, pay to publish*: the wall stood between a
 * customer and the first time they saw their own site on its own address, which is the moment the
 * whole thing becomes real to them. Now the wall stands between them and the site STAYING there —
 * they have already seen it work, sent the link to somebody, and what they are buying is something
 * they have held.
 *
 * ⚠️ SEVEN DAYS IS A NUMBER WITH A JOB: it is long enough to show the site to a partner and a
 * customer over a weekend, and short enough that a site nobody came back for is off the internet
 * inside a week rather than sitting under our certificate indefinitely.
 *
 * ⚠️ AND IT IS GRANTED ONCE PER SITE, WHICH IS THE PART THAT MATTERS. The expiry is stamped on the
 * first publish and carried forward by every publish after it, so a customer who cancels and
 * republishes does not get another week — the loop that would otherwise make this free for ever
 * closes itself, with no counter to keep and nothing to reconcile.
 *
 * One declaration, three consumers (§0.5): the sweep that takes a site down, the card that counts
 * the days remaining, and the dialog that says so the moment a site goes live.
 */
export const TRIAL = {
  days: 7,
  ms: 7 * 24 * 60 * 60 * 1000,
} as const
